import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuHouse } from 'react-icons/lu';
import classNames from 'classnames';

import { logEvent } from '~/config/firebase.config';
import { PUBLIC_DEMO_CLIENTS } from '~/config/gameParamsMemory';

import { Mode } from '~/types/client';
import { Button } from '~/components/ui/button';
import { SettingsButton } from '~/components/SettingsButton';
import { FullscreenButton } from '~/components/design/buttons/FullscreenButton';
import { MuteButton } from '~/components/design/buttons/MuteButton';
import { PauseButton } from '~/components/design/buttons/PauseButton';
import { QRCodeButton } from '~/components/design/buttons/QRCodeButton';
import { DeleteScoresButton } from '~/components/design/buttons/DeleteScoresButton';
import { ModeButton } from '~/components/design/buttons/ModeButton';

import styles from './ControlPanel.module.css';

export type ControlPanelProps = {
    preset?: Mode;
    direction?: 'left' | 'right' | 'top' | 'bottom';
    corner?: 'bottomRight' | 'bottomLeft' | 'topRight' | 'topLeft';
    clientID?: string;
    gameID?: string;
    /** Animator Control : un animateur pilote depuis son téléphone → on masque le
        bouton Pause (la télécommande l'a). « Menu » et QR restent TOUJOURS visibles :
        cachés, on ne savait plus revenir au menu ni montrer le QR (parcours du 20/10). */
    minimal?: boolean;
};

export function ControlPanel({
    corner = 'bottomRight',
    direction = 'left',
    preset = Mode.Anim,
    clientID,
    gameID,
    minimal = false,
}: ControlPanelProps) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Mesure du parcours entre deux jeux (GA4) : aucun clic de la TV n'était compté.
    // Pas la démo publique (elle fausserait la mesure des essais).
    const track = (name: string, extra: Record<string, unknown> = {}) => {
        if (preset === Mode.Demo || !clientID || PUBLIC_DEMO_CLIENTS.has(clientID)) return;
        logEvent(name, clientID, { clientID, gameID, minimal, ...extra });
    };
    const goHome = () => {
        track('tv_menu_click');
        navigate(preset === Mode.Demo ? '/demo' : '/home');
    };
    return (
        <div className={classNames(styles.container, styles[`dir-${direction}`], corner, 'z5')}>
            {preset !== Mode.Auto && (
                <>
                    <Button preset="secondary" icon={<LuHouse />} onClick={goHome}>
                        {t('console.menu')}
                    </Button>
                    {/* Pause masquée (pas démontée) quand la télécommande pilote. */}
                    <span style={{ display: minimal ? 'none' : 'contents' }}>
                        <PauseButton onToggle={(paused) => track('tv_pause_click', { paused })} />
                    </span>
                    <QRCodeButton onToggle={(open) => track('tv_qr_click', { open })} />
                </>
            )}
            <SettingsButton direction={direction}>
                {preset === Mode.Auto && (
                    <>
                        <Button preset="secondary" icon={<LuHouse />} onClick={() => navigate('/home')} />
                        <PauseButton />
                        <QRCodeButton />
                    </>
                )}
                <FullscreenButton />
                <MuteButton />
                {clientID && gameID && <DeleteScoresButton clientID={clientID} gameID={gameID} />}
                {/* Toggle de mode session : réservé aux offres bars/camping (anim). Masqué
                    pour les aéroports (auto) où basculer le mode casserait le kiosque free-play. */}
                {preset !== Mode.Auto && <ModeButton />}
            </SettingsButton>
        </div>
    );
}
